import { HelmetContainer } from '_components/atoms';
import React from 'react';

const Privacy: React.FC = () => {
  return (
    <div className="max-w-3xl mx-auto p-6 text-gray-800">
      <HelmetContainer
        title="Privacy Policy | Modesto On Ice"
        description="How Modesto On Ice collects and uses information when you book skating tickets, lessons, passes, or parties on the website or in the mobile app."
        url="/privacy"
      />

      <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
      <p className="text-sm text-gray-500 mb-8">
        Last updated: October 7, 2026
      </p>

      <div className="space-y-6 leading-relaxed">
        <p>
          Modesto On Ice, Inc. (“Modesto On Ice,” “we,” “us”) operates the
          ticket website at tickets.modestoonice.com and the Modesto On Ice
          mobile app. This policy describes what we collect when you book
          skating sessions, lessons, passes, or parties, sign a waiver, or use a
          staff account.
        </p>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Information you give us
          </h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <span className="font-medium">Account.</span> Name, email address,
              password (stored by our sign-in provider), and, if you add them,
              phone number and date of birth.
            </li>
            <li>
              <span className="font-medium">Booking.</span> Skater names and
              dates of birth, session or product choices, party details, and
              coupon codes. A parent or legal guardian provides this information
              for skaters under 18.
            </li>
            <li>
              <span className="font-medium">Waiver.</span> Name, contact
              details, date of birth, emergency contact, and your signature.
            </li>
            <li>
              <span className="font-medium">Payment.</span> Card details are
              entered in Square’s payment form. We receive a payment
              confirmation and a token from Square. We do not store your full
              card number on our servers.
            </li>
            <li>
              <span className="font-medium">Support.</span> Anything you send to
              modestoonice@gmail.com or tell our staff by phone.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Information collected automatically
          </h2>
          <p>
            The website and app talk to our servers over HTTPS to load seasons,
            prices, and your bookings. We do not use them to track you across
            other companies’ apps or websites, and we do not show third-party
            ads.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            How we use it
          </h2>
          <p>
            We use this information to create and manage your account, sell and
            deliver admissions, show your barcode at check-in, keep required
            waiver records, process refunds or customer-service requests, and
            prevent fraud.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Who we share it with
          </h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>Amazon Web Services, which hosts sign-in.</li>
            <li>Square, which processes card payments.</li>
            <li>
              Rink staff, who see bookings and waivers in order to check you in.
            </li>
          </ul>
          <p className="mt-2">We do not sell personal information.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Children</h2>
          <p>
            The website and app are for teens and adults booking a visit.
            Skaters can be as young as 3. We do not knowingly let a child create
            their own account. A parent or legal guardian enters a child’s name
            and date of birth and signs the waiver. Contact us if you believe a
            child created an account without a guardian.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Your choices
          </h2>
          <p>
            You can browse booking screens as a guest. You can update your phone
            number and date of birth in Profile. To close your account, use
            Delete Account on the Account screen in the mobile app, or open
            Profile on this website while signed in. Closing your account
            removes your sign-in. Bookings, waivers, and payment records stay on
            file. You can also email modestoonice@gmail.com with the subject
            “Delete my account” from the address on the account.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Security</h2>
          <p>
            We transmit data over HTTPS. No method of storage or transmission is
            completely secure.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Changes</h2>
          <p>We will post updates on this page and change the date above.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Contact</h2>
          <p>
            Modesto On Ice, Inc.
            <br />
            Corner of K and 11th Streets, Modesto, CA
            <br />
            <a
              href="mailto:modestoonice@gmail.com"
              className="text-blue-700 hover:underline"
            >
              modestoonice@gmail.com
            </a>
            <br />
            <a
              href="tel:+12095051181"
              className="text-blue-700 hover:underline"
            >
              (209) 505-1181
            </a>
          </p>
        </section>
      </div>
    </div>
  );
};

export default Privacy;
